import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { teamsService, playersService } from '../../../services/supabaseService';

const TeamManagementTab = ({ tournamentId }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [isLinkingTeam, setIsLinkingTeam] = useState(false);
  const [selectedTeamToLink, setSelectedTeamToLink] = useState('');
  const [teamError, setTeamError] = useState('');
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    role: '',
    jerseyNumber: ''
  });

  useEffect(() => {
    if (tournamentId && tournamentId !== 'new') {
      fetchTeamsAndPlayers();
      fetchAllTeams();
    }
  }, [tournamentId]);

  const fetchAllTeams = async () => {
    try {
      const data = await teamsService?.getAll();
      setAllTeams(data || []);
    } catch (error) {
      console.error('Error fetching all teams:', error);
    }
  };

  const fetchTeamsAndPlayers = async () => {
    setLoading(true);
    try {
      const tournamentTeams = await teamsService?.getByTournament(tournamentId);
      
      const teamsWithPlayers = await Promise.all(
        tournamentTeams?.map(async (tt) => {
          // Fetch tournament-specific squad instead of base squad
          const squadPlayers = await playersService?.getTournamentSquad(tournamentId, tt?.teams?.id);
          return {
            id: tt?.teams?.id,
            name: tt?.teams?.name,
            shortName: tt?.teams?.country,
            logo: tt?.teams?.logo || "https://img.rocket.new/generatedImages/rocket_gen_img_16b616325-1766600649637.png",
            logoAlt: tt?.teams?.logoAlt || `${tt?.teams?.name} team logo`,
            players: squadPlayers?.map(p => ({
              id: p?.playerId,
              name: p?.name,
              role: p?.role?.toLowerCase()?.replace(' ', '-'),
              jerseyNumber: p?.jerseyNumber || 0
            })) || []
          };
        }) || []
      );

      setTeams(teamsWithPlayers);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const playerRoleOptions = [
  { value: 'Batter', label: 'Batter' },
  { value: 'Bowler', label: 'Bowler' },
  { value: 'Wicket Keeper', label: 'Wicket Keeper' },
  { value: 'All-rounder', label: 'All Rounder' }];


  const getRoleIcon = (role) => {
    const roleMap = {
      'Batter': 'Target',
      'Bowler': 'Zap',
      'Wicket Keeper': 'Shield',
      'All-rounder': 'Star'
    };
    return roleMap?.[role] || 'User';
  };

  const getRoleBadgeColor = (role) => {
    const colorMap = {
      'Batter': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Bowler': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Wicket Keeper': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'All-rounder': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    };
    return colorMap?.[role] || 'bg-gray-100 text-gray-800';
  };

  const handleAddPlayer = async () => {
    if (!newPlayer?.name || !newPlayer?.role || !newPlayer?.jerseyNumber) {
      return;
    }

    try {
      const roleDisplayMap = {
        'Batter': 'Batter',
        'Bowler': 'Bowler',
        'Wicket Keeper': 'Wicket Keeper',
        'All-rounder': 'All-rounder'
      };

      const playerData = {
        name: newPlayer?.name,
        teamId: selectedTeam,
        role: roleDisplayMap?.[newPlayer?.role]
      };

      const createdPlayer = await playersService?.create(playerData);

      if (createdPlayer) {
        const updatedTeams = teams?.map((team) => {
          if (team?.id === selectedTeam) {
            return {
              ...team,
              players: [
                ...team?.players,
                {
                  id: createdPlayer?.id,
                  name: createdPlayer?.name,
                  role: newPlayer?.role,
                  jerseyNumber: parseInt(newPlayer?.jerseyNumber)
                }
              ]
            };
          }
          return team;
        });

        setTeams(updatedTeams);
        setNewPlayer({ name: '', role: '', jerseyNumber: '' });
        setIsAddingPlayer(false);
      }
    } catch (error) {
      console.error('Error adding player:', error);
    }
  };

  const handleLinkTeam = async () => {
    if (!selectedTeamToLink) {
      setTeamError('Please select a team to link');
      return;
    }

    setTeamError('');

    try {
      const tournamentTeams = await teamsService?.getByTournament(tournamentId);
      const isAlreadyLinked = tournamentTeams?.some(tt => tt?.teams?.id === selectedTeamToLink);

      if (isAlreadyLinked) {
        setTeamError('This team is already linked to the tournament');
        return;
      }

      // Link team to tournament
      await teamsService?.addToTournament(tournamentId, selectedTeamToLink);
      
      // Fetch all players from the team
      const teamPlayers = await playersService?.getByTeam(selectedTeamToLink);
      
      // Add all players to tournament squad
      if (teamPlayers && teamPlayers?.length > 0) {
        await Promise.all(
          teamPlayers?.map(player => 
            playersService?.addToTournamentSquad(tournamentId, selectedTeamToLink, player?.id)
          )
        );
      }
      
      await fetchTeamsAndPlayers();
      setIsLinkingTeam(false);
      setSelectedTeamToLink('');
    } catch (error) {
      console.error('Error linking team:', error);
      setTeamError('Failed to link team. Please try again.');
    }
  };

  const handleManageSquad = (teamId) => {
    navigate(`/tournament-squad-management?tournamentId=${tournamentId}&teamId=${teamId}`);
  };

  const availableTeamsToLink = allTeams?.filter(
    team => !teams?.some(t => t?.id === team?.id)
  );

  const handleRemovePlayer = async (teamId, playerId) => {
    try {
      const success = await playersService?.delete(playerId);
      
      if (success) {
        const updatedTeams = teams?.map((team) => {
          if (team?.id === teamId) {
            return {
              ...team,
              players: team?.players?.filter((p) => p?.id !== playerId)
            };
          }
          return team;
        });
        setTeams(updatedTeams);
      }
    } catch (error) {
      console.error('Error removing player:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Participating Teams</h3>
          <p className="text-sm text-muted-foreground mt-1">Link teams to tournament and manage squads</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            iconName="Users"
            iconPosition="left"
            className="flex-1 sm:flex-initial"
            onClick={() => navigate('/global-team-management')}>
            Manage Teams
          </Button>
          <Button
            variant="default"
            iconName="Link"
            iconPosition="left"
            className="flex-1 sm:flex-initial"
            onClick={() => setIsLinkingTeam(true)}>
            Link Team
          </Button>
        </div>
      </div>

      {isLinkingTeam && (
        <div className="bg-card border border-border rounded-lg p-4 md:p-6 space-y-4">
          <h4 className="text-base md:text-lg font-semibold text-foreground">Link Existing Team</h4>
          {teamError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg text-sm">
              {teamError}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Team
              </label>
              <select
                value={selectedTeamToLink}
                onChange={(e) => setSelectedTeamToLink(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="">Choose a team...</option>
                {availableTeamsToLink?.map(team => (
                  <option key={team?.id} value={team?.id}>
                    {team?.name} ({team?.country})
                  </option>
                ))}
              </select>
              {availableTeamsToLink?.length === 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  All teams are already linked. Create new teams in Global Team Management.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleLinkTeam}
                iconName="Check"
                iconPosition="left"
                className="flex-1"
                disabled={!selectedTeamToLink}>
                Link Team
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsLinkingTeam(false);
                  setSelectedTeamToLink('');
                  setTeamError('');
                }}
                iconName="X"
                iconPosition="left"
                className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {teams?.map((team) => (
          <div key={team?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 md:p-6 bg-muted border-b border-border">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden bg-background flex-shrink-0">
                  <Image
                    src={team?.logo}
                    alt={team?.logoAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base md:text-lg font-semibold text-foreground truncate">{team?.name}</h4>
                  <p className="text-sm text-muted-foreground">{team?.shortName} • {team?.players?.length} Players</p>
                </div>
              </div>
            </div>

            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h5 className="text-sm font-medium text-foreground">Tournament Squad</h5>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Settings"
                  iconPosition="left"
                  onClick={() => handleManageSquad(team?.id)}>
                  Manage Squad
                </Button>
              </div>

              <div className="space-y-2">
                {team?.players?.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Icon name="Users" className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No players in tournament squad</p>
                    <p className="text-xs mt-1">Manage squad to add players</p>
                  </div>
                ) : (
                  team?.players?.slice(0, 5)?.map((player) => (
                    <div
                      key={player?.id}
                      className="flex items-center justify-between p-3 bg-background rounded-lg">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {player?.jerseyNumber}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{player?.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(player?.role)}`}>
                              <Icon name={getRoleIcon(player?.role)} size={12} />
                              {player?.role?.replace('-', ' ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {team?.players?.length > 5 && (
                  <p className="text-xs text-center text-muted-foreground pt-2">
                    +{team?.players?.length - 5} more players
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {teams?.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Teams Linked</h3>
          <p className="text-muted-foreground mb-4">
            Link existing teams to this tournament or create new teams
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              iconName="Users"
              iconPosition="left"
              onClick={() => navigate('/global-team-management')}>
              Manage Teams
            </Button>
            <Button
              variant="default"
              iconName="Link"
              iconPosition="left"
              onClick={() => setIsLinkingTeam(true)}>
              Link Team
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementTab;